package com.nonopn.skywatcher.common.app.pages.map.model

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DrawerState
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.nonopn.geolocation.GpsLocation
import com.nonopn.geolocation.GpsResult
import com.nonopn.geolocation.TrackingStatus
import com.nonopn.skywatcher.app.utils.Clock
import com.nonopn.skywatcher.app.utils.Queue
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.common.app.api.impl.ThunderDetectionLocation
import com.nonopn.skywatcher.common.app.api.impl.ThunderDetectionSurroundingContext
import com.nonopn.skywatcher.common.app.api.impl.ThunderDetectionSurroundingUser
import com.nonopn.skywatcher.common.app.api.impl.WarnStateValidationState
import com.nonopn.skywatcher.common.app.home.AppModel
import com.nonopn.skywatcher.common.app.pages.map.model.forecast.ForecastAggregator
import com.nonopn.skywatcher.common.app.pages.map.model.weather.AlertType
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ProbeStatus
import com.nonopn.skywatcher.common.app.pages.map.model.weather.SortBy
import com.nonopn.skywatcher.common.app.pages.map.model.weather.SortByInstance
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ThunderActivityCondensed
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ThunderActivityInfo
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ThunderDetectionActivity
import com.nonopn.skywatcher.common.app.pages.map.model.weather.toCondensed
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetection
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetectionWithMetadata
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.now
import com.nonopn.skywatcher.common.app.res.now_after
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.get
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.maps.MapUtils
import com.nonopn.skywatcher.maps.providers.VaisalaProviderOptions
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.platform.Platform
import eu.codlab.platform.currentPlatform
import eu.codlab.sentry.wrapper.Sentry
import eu.codlab.viewmodel.StateHandler
import eu.codlab.viewmodel.StateViewModel
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import korlibs.io.async.delay
import korlibs.time.DateTime
import korlibs.time.seconds
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.channels.consumeEach
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import ovh.plrapps.mapcompose.api.ExperimentalClusteringApi
import ovh.plrapps.mapcompose.api.addLayer
import ovh.plrapps.mapcompose.api.addMarker
import ovh.plrapps.mapcompose.api.addPath
import ovh.plrapps.mapcompose.api.centroidSnapshotFlow
import ovh.plrapps.mapcompose.api.enableFlingZoom
import ovh.plrapps.mapcompose.api.enableRotation
import ovh.plrapps.mapcompose.api.enableScrolling
import ovh.plrapps.mapcompose.api.enableZooming
import ovh.plrapps.mapcompose.api.maxScale
import ovh.plrapps.mapcompose.api.minScale
import ovh.plrapps.mapcompose.api.removeLayer
import ovh.plrapps.mapcompose.api.removeMarker
import ovh.plrapps.mapcompose.api.removePath
import ovh.plrapps.mapcompose.api.scale
import ovh.plrapps.mapcompose.api.setLayerOpacity
import ovh.plrapps.mapcompose.api.setMagnifyingFactor
import ovh.plrapps.mapcompose.api.snapScrollTo
import ovh.plrapps.mapcompose.core.TileStreamProvider
import ovh.plrapps.mapcompose.ui.state.MapState
import kotlin.coroutines.Continuation
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.time.Duration.Companion.minutes

val MAX_ZOOM = 8.0
val ZOOM_PIN = 1.0
val MIN_ZOOM = 0.0001
val DEFAULT_ZOOM = 0.01

data class MainScreenModelState(
    val showPollutants: Boolean = true,
    val initialized: Boolean = false,
    val currentCoordinates: LatLng? = null,
    val updatingCoordinates: Boolean = false,
    val aggregator: ForecastAggregator? = null,
    val probeStatus: ProbeStatus? = null,
    val lightningActivity: List<ThunderDetectionActivity> = emptyList(),
    val lightninActivityCondensed: List<ThunderActivityCondensed> = emptyList(),
    val lightningActivityCondensedSortedBy: SortByInstance = SortByInstance(),
    val usersOnMap: ThunderDetectionSurroundingContext = ThunderDetectionSurroundingContext(),
    val needUserConfirmationAlertSeen: Boolean = false,
    val path: List<LatLng> = emptyList(),
    val lightningActivityInfo: ThunderActivityInfo? = null,
    val setOfVaisalaOptions: Set<VaisalaProviderOptions> = emptySet(),
    val startedGps: Boolean = false
)

interface MainScreenModel : StateHandler<MainScreenModelState> {
    fun configureMapState()
    fun zoomIn()
    fun zoomOut()
    fun moveToOrigin()
    fun closeLightningDrawer()
    fun openLightningDrawer()

    fun setShowSatellite(show: Boolean)

    fun setShowStormCells(show: Boolean)

    fun sortLightningActivityBy(sortBy: SortBy)
    fun confirmAlertSeen()
    fun sendManualAlert(alertType: AlertType)

    val levelCount: Int
    val fullWidthOrHeight: Int
    val mapState: MapState

    var lightningDrawerState: DrawerState?
    var scope: CoroutineScope?
}

class MainScreenModelImpl(
    private val appModel: AppModel,
    private val thunderDetection: ThunderDetectionWithMetadata,
) : StateViewModel<MainScreenModelState>(
    MainScreenModelState(
        currentCoordinates = thunderDetection.detection.position(),
        lightningActivityInfo = ThunderActivityInfo.from(
            thunderDetection,
            emptyList() // thunderDetection.fake?.lightningActivity
        ),
        path = emptyList() // thunderDetection.fakePath ?: emptyList()
    )
),
    MainScreenModel {

    private val gpsLocation = GpsLocation()
    private var cachedPosition: LatLng? = null
    private val thunderDetectionComposer: ComposeProbeHolder
    private val clock = Clock(viewModelScope)
    private val googleApi = appModel.googleApi
    private var vaisalaApi = appModel.vaisalaApi
    private val weatherForecast = appModel.states.value.currentApi!!

    private val tileStreamProvider = TileStreamProvider { row, col, zoomLvl ->
        googleApi.getTile(row, col, zoomLvl)
    }

    private var tileVaisalaStreamProvider = TileStreamProvider { row, col, zoomLvl ->
        vaisalaApi.getTile(row, col, zoomLvl)
    }

    private var tileVaisalaLayerId: String? = null

    override val levelCount = 18
    override val fullWidthOrHeight = 256 * 2.0.pow(levelCount).toInt()
    override val mapState = MapState(
        levelCount,
        fullWidthOrHeight,
        fullWidthOrHeight,
        tileSize = 256
    ).apply {
        setMagnifyingFactor(1)

        maxScale = MAX_ZOOM
        scale = DEFAULT_ZOOM
        addLayer(tileStreamProvider)



        enableRotation()
    }

    init {
        println("CREATING NEW MainScreenModel")
        val latLng = thunderDetection.detection.position()
        val xy = MapUtils.latLngToXY(latLng)

        thunderDetectionComposer = ComposeProbeHolder(
            mapState = mapState,
            flushCallouts = ::flushCallouts
        )

        safeLaunch {
            mapState.resetVaisalaTileProvider()

            updateState { copy(initialized = true) }

            /*if (null != thunderDetection.fake) {
                var newId = 0
                mapState.onTap { x, y ->
                    newId++
                    val latLng = MapUtils.xyToLatLng(x, y)

                    // fakePoints.add(
                    //    Triple(x, y, DateTime.nowUnixMillisLong() / 1000 - (newId * 30).toLong())
                    // )

                    //val lightningActivity =
                    //    states.value.lightningActivity + ThunderDetectionActivity(
                    //        0,
                    //        "$newId",
                    //        latLng.latitude,
                    //        latLng.longitude,
                    //        DateTime.nowUnixMillisLong() / 1000 - (newId * 30).toLong()
                    //    )
                    //val usersOnMap = states.value.usersOnMap + (latLng to false)

                    //val path = states.value.path + latLng
                    //path.forEach {
                    //    println(it)
                    //}

                    //onNewUsersLoaded(usersOnMap)
                    //showStormLocation(path)
                    //lightningActivity.forEach { showLightningActivity(it) }

                    //showPathLocation(path)

                    //updateState { copy(path = path) }
                    //updateState { copy(lightningActivity = lightningActivity) }
                    //updateState { copy(usersOnMap = usersOnMap) }
                }
            }*/

            loadData()
            postLoadNewCoordinates()

            startLoadingUsersOnMap()

            // showPathLocation(thunderDetection.fakePath ?: emptyList())
            // showStormLocation(thunderDetection.fakeStormPath ?: emptyList())

            mapState.snapScrollTo(
                xy.x,
                xy.y
            )

            safeLaunch {
                clock.start(2.minutes).collect {
                    startLoadingUsersOnMap()
                    postLoadNewCoordinates()
                }
            }

            safeLaunch {
                clock.start(5.minutes).collect {
                    mapState.resetVaisalaTileProvider()
                }
            }

            showThunderDetection()

            mapState.centroidSnapshotFlow().collect {
                // TODO update the data ?
                val coords = MapUtils.xyToLatLng(it.x, it.y)
                updateState { copy(currentCoordinates = coords) }
            }
        }

        safeLaunch(
            onError = {
                it.printStackTrace()
            }
        ) {
            while (isActive) {
                appModel.states.value.currentApi?.registerDetectionUpdate(thunderDetection) {
                    incoming.consumeEach { frame ->
                        if (frame is Frame.Text) {
                            println("receiving incoming data ${frame.readText()}")

                            launch {
                                startLoadingUsersOnMap()
                                postLoadNewCoordinates()
                            }
                        }
                    }
                }
                delay(5.seconds)
                println("websocket closed !")
            }
        }
    }

    private suspend fun attemptTracking(): LatLng? {
        if (states.value.startedGps) return cachedPosition

        return suspendCoroutine { continuation ->
            safeLaunch {
                var continuationCopy: Continuation<LatLng?>? = continuation
                updateState { copy(startedGps = true) }

                println("GPS obtained ?")

                when (val loc = gpsLocation.startTracking()) {
                    is TrackingStatus.Success -> {
                        cachedPosition = loc.location.coordinates.let {
                            LatLng(
                                latitude = it.latitude,
                                longitude = it.longitude
                            )
                        }
                    }

                    else -> {
                        // nothing
                    }
                }

                println("GPS make attempt")
                makeAnAttemptForGpsLocation()

                // now resume
                if (null != cachedPosition) {
                    continuationCopy?.resume(cachedPosition)
                    continuationCopy = null
                }

                println("GPS obtained value !")
                gpsLocation.updates()?.collect { result ->
                    println("GPS $result")

                    continuationCopy?.resume(cachedPosition)
                    continuationCopy = null

                    cachedPosition = LatLng(
                        latitude = result.latitude,
                        longitude = result.longitude
                    )
                }

                gpsLocation.stopTracking()
            }


        }
    }

    override fun onCleared() {
        println("CLEARED MainScreenModel")

        super.onCleared()
    }

    private suspend fun MapState.resetVaisalaTileProvider() {
        tileVaisalaLayerId?.let {
            removeLayer(it)
        }

        vaisalaApi.reset()

        tileVaisalaLayerId = addLayer(tileVaisalaStreamProvider).also {
            setLayerOpacity(it, 0.5f)
        }
    }

    override var lightningDrawerState: DrawerState? = null
    override var scope: CoroutineScope? = null

    override fun configureMapState() {
        mapState.enableZooming()
        mapState.enableScrolling()
        mapState.enableFlingZoom()
    }

    override fun zoomIn() = applyZoomCoef(1.1f)
    override fun zoomOut() = applyZoomCoef(0.9f)

    private fun applyZoomCoef(coef: Float) {
        val newZoom = mapState.scale * coef

        applyZoom(newZoom)
    }

    private fun applyZoom(newZoom: Double, latLng: LatLng? = null) {
        // make sure that we are both 3.0f > scale > 0.05f
        mapState.scale = max(min(newZoom, MAX_ZOOM), MIN_ZOOM)

        println("apply coef ${mapState.minScale} ${mapState.maxScale} $newZoom $MAX_ZOOM ${mapState.scale}")
        println("laoding coordinates ? ${states.value.currentCoordinates}")
        safeLaunch {
            val coords = latLng ?: states.value.currentCoordinates

            coords!!.let {
                println("having coordinates ? $it")
                val coords = MapUtils.latLngToXY(it.latitude, it.longitude)
                println("snapTo $coords")
                mapState.snapScrollTo(coords.x, coords.y)
            }
        }
    }

    private fun loadData() = safeLaunch(
        onError = {
            it.printStackTrace()
        }
    ) {
        println("loading data")
        val coords = thunderDetection.detection.position()

        updateState { copy(currentCoordinates = coords, updatingCoordinates = true) }

        listOf(
            10000 to AppColor.Red suffix AppColor.RedSemiTransparent,
            30000 to AppColor.Orange suffix AppColor.OrangeSemiTransparent,
            50000 to AppColor.Green suffix AppColor.GreenSemiTransparent
        ).forEach { (distance, color, fillColor) ->
            val coordinates = MapUtils.delta(coords, distance, 45.0).toXY()

            mapState.addMarker("distance_$distance", coordinates.x, coordinates.y) {
                DefaultCard(
                    backgroundColor = color,
                    columnModifier = Modifier.padding(4.dp)
                ) {
                    TextNormal("${(distance / 1000.0).roundToInt()}Km", color = AppColor.WhiteCream)
                }
            }

            mapState.addPath(
                "circle_$distance",
                width = 4.dp,
                color = color,
                fillColor = fillColor,
            ) {
                MapUtils.circle(coords, distance) {
                    MapUtils.latLngToXY(this).toPair()
                }?.let { list -> this.addPoints(list) }
            }
        }
    }

    override fun moveToOrigin() {
        mapState.scale = DEFAULT_ZOOM

        safeLaunch {
            val latLng = thunderDetection.detection.position()
            val xy = MapUtils.latLngToXY(latLng)

            updateState { copy(initialized = true) }

            mapState.snapScrollTo(
                xy.x,
                xy.y
            )
        }
    }

    override fun closeLightningDrawer() = openOrCloseDrawer(false)

    override fun openLightningDrawer() = openOrCloseDrawer(true)

    override fun setShowSatellite(show: Boolean) {
        safeLaunch {
            val set = if (show) {
                states.value.setOfVaisalaOptions + VaisalaProviderOptions.Satellite
            } else {
                states.value.setOfVaisalaOptions - VaisalaProviderOptions.Satellite
            }
            updateState { copy(setOfVaisalaOptions = set) }
            vaisalaApi.options = set.toList()
            mapState.resetVaisalaTileProvider()
        }
    }

    override fun setShowStormCells(show: Boolean) {
        safeLaunch {
            val set = if (show) {
                states.value.setOfVaisalaOptions + VaisalaProviderOptions.StormCells
            } else {
                states.value.setOfVaisalaOptions - VaisalaProviderOptions.StormCells
            }
            updateState { copy(setOfVaisalaOptions = set) }
            vaisalaApi.options = set.toList()
            mapState.resetVaisalaTileProvider()
        }
    }

    override fun sortLightningActivityBy(sortBy: SortBy) {
        safeLaunch {
            updateState {
                lightningActivityCondensedSortedBy.next(sortBy).let { sortBy ->
                    copy(
                        lightningActivityCondensedSortedBy = sortBy,
                        lightninActivityCondensed = lightninActivityCondensed.sortBy(sortBy)
                    )
                }
            }
        }
    }

    private fun openOrCloseDrawer(isOpen: Boolean) {
        safeLaunch {
            scope?.let {
                withContext(it.coroutineContext) {
                    if (isOpen) {
                        lightningDrawerState?.open()
                    } else {
                        lightningDrawerState?.close()
                    }
                }
            }
        }
    }

    private suspend fun makeAnAttemptForGpsLocation() {
        if (currentPlatform != Platform.IOS &&
            currentPlatform != Platform.ANDROID &&
            currentPlatform != Platform.JS
        ) {
            return
        }

        if (null == cachedPosition) {
            val loc = gpsLocation.current()
            if (loc is GpsResult.Success) {
                cachedPosition = loc.location.coordinates.let {
                    LatLng(
                        latitude = it.latitude,
                        longitude = it.longitude
                    )
                }
            }
        }
    }

    private val queue = Queue(viewModelScope)

    private fun attemptSendPosition(position: LatLng?) {
        safeLaunch {
            queue.enqueue {
                try {
                    if (currentPlatform != Platform.IOS &&
                        currentPlatform != Platform.ANDROID &&
                        currentPlatform != Platform.JS
                    ) {
                        return@enqueue
                    }

                    makeAnAttemptForGpsLocation()

                    if (null == position) {
                        println("GPS position won't be updated")
                        return@enqueue
                    }

                    val latLng = ThunderDetectionLocation(
                        latitude = position.latitude,
                        longitude = position.longitude
                    )

                    println("updating the position for the current user -> $latLng")
                    val result = appModel.states.value.currentApi?.updateLocation(latLng)
                    println("  and result is $result")
                } catch (err: Throwable) {
                    err.printStackTrace()
                    Sentry.captureException(err)
                }
            }
        }
    }

    private fun postLoadNewCoordinates() {
        safeLaunch {
            updateState {
                copy(
                    updatingCoordinates = true,
                )
            }

            // we update the location on mobiles
            attemptSendPosition(attemptTracking())

            val probeStatus: ProbeStatus = weatherForecast.status(thunderDetection)

            val lightningActivity = probeStatus.lightningActivity

            (0..5).forEach { println(">>>>>>>>>>>>>>>>>") }
            lightningActivity.forEach {
                println("having impact -> $it")
            }
            (0..5).forEach { println("<<<<<<<<<<<<<<<<<") }

            states.value.lightningActivity.forEach {
                mapState.removeMarker(it.externalId)
            }

            lightningActivity.forEach {
                showLightningActivity(it)
            }

            val aggregator = ForecastAggregator(
                probeStatus.report,
                Res.string.now.get(),
                Res.string.now_after.get()
            )

            val currentSeconds = DateTime.nowUnixMillisLong() / 1000

            val lightninActivityCondensed = lightningActivity.map {
                it.toCondensed(
                    thunderDetection.detection.position(),
                    currentSeconds
                )
            }.sortedBy { it.distanceKm }

            updateState {
                copy(
                    updatingCoordinates = false,
                    aggregator = aggregator,
                    probeStatus = probeStatus,
                    lightningActivity = lightningActivity,
                    lightninActivityCondensed =
                        lightninActivityCondensed.sortBy(lightningActivityCondensedSortedBy),
                    lightningActivityInfo = ThunderActivityInfo.from(
                        thunderDetection,
                        lightningActivity
                    ),
                )
            }
        }
    }

    override fun confirmAlertSeen() {
        safeLaunch {
            try {
                val context = appModel.states.value.currentApi!!.confirmAlertSeen(thunderDetection)

                onNewUsersLoaded(context.users)

                updateState {
                    copy(
                        usersOnMap = context,
                    )
                }
            } catch (err: Throwable) {
                err.printStackTrace()
            }
        }
    }

    override fun sendManualAlert(alertType: AlertType) {
        safeLaunch {
            try {
                val context =
                    appModel.states.value.currentApi!!.sendManualAlert(thunderDetection, alertType)

                onNewUsersLoaded(context.users)

                updateState { copy(usersOnMap = context) }

                postLoadNewCoordinates()
            } catch (err: Throwable) {
                err.printStackTrace()
            }
        }
    }

    private fun startLoadingUsersOnMap() = safeLaunch {
        try {
            val context = appModel.states.value.currentApi!!.surroundingUsers(thunderDetection)

            println("startLoadingUsersOnMap $context")
            updateState { copy(usersOnMap = context) }

            onNewUsersLoaded(context.users)
        } catch (err: Throwable) {
            err.printStackTrace()
        }
    }

    private fun onNewUsersLoaded(users: List<ThunderDetectionSurroundingUser>) {
        states.value.usersOnMap.users.forEachIndexed { index, user ->
            mapState.removeMarker("user_${user.id}")
        }

        users.forEach { user ->
            val latLng = LatLng(user.latitude, user.longitude)
            val xy = MapUtils.latLngToXY(latLng)

            mapState.addMarker(
                "user_${user.id}",
                xy.x,
                xy.y,
                c = composableUserONnMap(latLng, user.alertState)
            )
        }
    }

    private fun showLightningActivity(activity: ThunderDetectionActivity) {
        val xy = MapUtils.latLngToXY(activity.latitude, activity.longitude)

        mapState.addMarker(activity.externalId, xy.x, xy.y, c = composable(activity))
    }

    @OptIn(ExperimentalClusteringApi::class)
    private fun showThunderDetection() {
        val detection = thunderDetection.detection
        val xy = MapUtils.latLngToXY(detection.position())

        mapState.addMarker(
            "thunder_detection",
            xy.x,
            xy.y,
            c = thunderDetectionComposer.compose(detection, xy) {
                applyZoom(ZOOM_PIN, detection.position())
            }
        )
    }

    private fun showStormLocation(path: List<LatLng>) {
        if (path.isEmpty()) return

        mapState.removePath("storm_location")
        mapState.addPath(
            "storm_location",
            width = 1.dp,
            color = AppColor.Orange,
            fillColor = AppColor.OrangeSemiTransparent
        ) {
            this.addPoints(
                path.map { latLng ->
                    val xy = MapUtils.latLngToXY(latLng.latitude, latLng.longitude)
                    xy.x to xy.y
                }
            )
        }
    }

    private fun showPathLocation(path: List<LatLng>) {
        if (path.isEmpty()) return

        mapState.removePath("path_location")
        mapState.addPath(
            "path_location",
            width = 1.dp,
            color = AppColor.Blue
        ) {
            this.addPoints(
                path.map { latLng ->
                    val xy = MapUtils.latLngToXY(latLng.latitude, latLng.longitude)
                    xy.x to xy.y
                }
            )
        }
    }

    private fun composable(activity: ThunderDetectionActivity): @Composable () -> Unit {
        return {
            Column(
                Modifier.width(8.dp).height(8.dp)
                    .clip(
                        RoundedCornerShape(
                            topStart = 4.dp,
                            topEnd = 4.dp,
                            bottomStart = 4.dp,
                            bottomEnd = 4.dp
                        )
                    )
                    .background(activity.activityAge().color)
            ) {
                // nothing
            }
        }
    }

    private fun composableUserONnMap(
        latLng: LatLng,
        alertState: WarnStateValidationState
    ): @Composable () -> Unit {
        return {
            Column(
                Modifier.width(10.dp).height(10.dp)
                    .clip(
                        RoundedCornerShape(
                            topStart = 5.dp,
                            topEnd = 5.dp,
                            bottomStart = 5.dp,
                            bottomEnd = 5.dp
                        )
                    )
                    .background(Color.Black),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(
                    Modifier.width(8.dp).height(8.dp)
                        .clip(
                            RoundedCornerShape(
                                topStart = 4.dp,
                                topEnd = 4.dp,
                                bottomStart = 4.dp,
                                bottomEnd = 4.dp
                            )
                        )
                        .background(
                            when (alertState) {
                                WarnStateValidationState.AllClear -> UserAllClear
                                WarnStateValidationState.NeedsConfirm -> UserDidntValidateTheAlert
                                WarnStateValidationState.Confirmed -> UserValidatedTheAlert
                            }
                        )
                ) {
                    // nothing
                }
            }
        }
    }

    private fun flushCallouts() {
        mapState.onPress()
    }

    private fun List<ThunderActivityCondensed>.sortBy(sort: SortByInstance): List<ThunderActivityCondensed> {
        return when (sort.sortBy) {
            SortBy.Distance -> sort.order.sort(this) { it.distanceKm }
            SortBy.Strength -> sort.order.sort(this) { it.intensityNumber }
            SortBy.Time -> sort.order.sort(this) { it.createdAtDiffAbsolute }
        }
    }
}

val UserValidatedTheAlert = AppColor.BlueLight
val UserDidntValidateTheAlert = AppColor.Blue
val UserAllClear = AppColor.Green

class FakeMainScreenModel(
    private val updating: Boolean = false,
    private val fakeProbeStatus: ProbeStatus?,
    private val usersInConstructor: ThunderDetectionSurroundingContext = ThunderDetectionSurroundingContext()
) : MainScreenModel {
    override fun configureMapState() = Unit
    override fun zoomIn() = Unit
    override fun zoomOut() = Unit
    override fun moveToOrigin() = Unit
    override fun closeLightningDrawer() = Unit
    override fun openLightningDrawer() = Unit
    override fun setShowSatellite(show: Boolean) = Unit
    override fun setShowStormCells(show: Boolean) = Unit
    override fun sortLightningActivityBy(sortBy: SortBy) = Unit
    override fun confirmAlertSeen() = Unit
    override fun sendManualAlert(alertType: AlertType) = Unit

    override val levelCount: Int = 8
    override val fullWidthOrHeight: Int = 6
    override val mapState: MapState
        get() {
            return MapState(levelCount, fullWidthOrHeight, fullWidthOrHeight)
        }
    override var lightningDrawerState: DrawerState? = null
    override var scope: CoroutineScope? = null

    override val states: StateFlow<MainScreenModelState>
        get() = MutableStateFlow(
            MainScreenModelState(
                updatingCoordinates = updating,
                usersOnMap = usersInConstructor,
                lightningActivityInfo = ThunderActivityInfo.from(
                    ThunderDetection.fakes.first(),
                    emptyList(), // ThunderDetection.fakes.first().fake?.lightningActivity
                ),
            )
        )

    override fun setState(state: MainScreenModelState) {
        // not implemented
    }

    override fun updateState(block: MainScreenModelState.() -> MainScreenModelState) {
        // not implemented
    }

}

infix fun <A, B, C> Pair<A, B>.suffix(that: C): Triple<A, B, C> =
    Triple(this.first, this.second, that)
